/**
 * ReportRenderPage.js — standalone report render route (Stages 1-4 + breadth).
 *
 * Mounts a report component WITHOUT MainPage, given ?code&type&level[&pdf_token],
 * and signals `data-render-complete="true"` once data + images + (for client/
 * example) the theme map have loaded — the selector headless Chromium waits on.
 *
 * type → component:  political → DistrictReportView (cd)
 *                    client   → InvariantDistrictReportView (cd/states/statesenate/statehouse)
 *                    example  → StackExampleClientReport   (same levels)
 *
 * Gates, in order: resolve code→props (async for seat levels) → validate the
 * scoped ?pdf_token (HMAC/TTL/binding, server-side) → the component's onReady
 * (its aggregate loading flag + map) → paint buffer → image drain → 25s backstop.
 * Any failure → error, no report (Chromium can't capture an unauthorised/partial
 * render — data-render-complete never flips true).
 */
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DistrictReportView from "./DistrictReportView";
import InvariantDistrictReportView from "./InvariantDistrictReportView";
import StackExampleClientReport from "./StackExampleClientReport";
import { resolveReportPropsAsync } from "./utils/reportResolve";

const MAX_WAIT_MS = 25000;
const PAINT_BUFFER_MS = 3500;
const IMG_DRAIN_MS = 8000;

const COMPONENTS = {
  political: DistrictReportView,
  client: InvariantDistrictReportView,
  example: StackExampleClientReport,
};

// isError=true also stamps data-render-error="true" so headless Chromium can bail
// FAST on a hard failure (bad code / resolve error / invalid token) instead of
// waiting the full render timeout. Transient states (resolving / checking) don't.
const notice = (msg, { color = "#374151", isError = false } = {}) => (
  <div
    data-render-complete="false"
    data-render-error={isError ? "true" : "false"}
    style={{ padding: 24, fontFamily: "system-ui, sans-serif", fontSize: 14, color }}
  >{msg}</div>
);

export default function ReportRenderPage() {
  const [params] = useSearchParams();
  const code = params.get("code");
  const type = params.get("type") || "political";
  const level = params.get("level") || "cd";
  const token = params.get("pdf_token");
  const clientLabel = params.get("client") || undefined; // branding only (component may ignore)

  const [resolved, setResolved] = useState(null);        // null = resolving | {props} | {error}
  const [tokenState, setTokenState] = useState("checking"); // checking | valid | invalid
  const [renderComplete, setRenderComplete] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const handleReady = useCallback(() => setDataReady(true), []);

  // ── Resolve code → props (async for statesenate/statehouse) ─────────────────
  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    setResolved(null);
    (async () => {
      const r = await resolveReportPropsAsync({ code, level, type });
      if (!cancelled) setResolved(r);
    })();
    return () => { cancelled = true; };
  }, [code, level, type]);

  const ok = !!resolved && !resolved.error && !!code;

  // ── Token validation — must pass before we render anything ──────────────────
  useEffect(() => {
    if (!ok) return;
    if (!token) { setTokenState("invalid"); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/report/validate-token/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, code, type }),
        });
        if (!cancelled) setTokenState(res.ok ? "valid" : "invalid");
      } catch {
        if (!cancelled) setTokenState("invalid");
      }
    })();
    return () => { cancelled = true; };
  }, [ok, token, code, type]);

  const canRender = ok && tokenState === "valid";

  // ── Render-complete sequence — only once resolved + authorised ──────────────
  useEffect(() => {
    if (!canRender) return;
    let cancelled = false;
    const t0 = Date.now();
    const ms = () => Date.now() - t0;

    const backstop = setTimeout(() => {
      if (cancelled) return;
      cancelled = true;
      console.warn(`[render] BACKSTOP fired at ${ms()}ms — signalling complete without full settle`);
      setRenderComplete(true);
    }, MAX_WAIT_MS);

    if (dataReady) {
      (async () => {
        console.log(`[render] data ready (onReady) at ${ms()}ms`);
        await new Promise((r) => setTimeout(r, PAINT_BUFFER_MS)); // paint buffer
        if (cancelled) return;
        const deadline = Date.now() + IMG_DRAIN_MS;               // image drain
        while (Date.now() < deadline) {
          if (cancelled) return;
          const imgs = Array.from(document.querySelectorAll("img"));
          const allDone = imgs.length > 0 &&
            imgs.every((img) => img.complete && (img.naturalWidth > 0 || img.naturalWidth === undefined));
          if (allDone) { console.log(`[render] images drained at ${ms()}ms (${imgs.length} imgs)`); break; }
          await new Promise((r) => setTimeout(r, 250));
        }
        if (cancelled) return;
        cancelled = true;
        clearTimeout(backstop);
        console.log(`[render] render-complete at ${ms()}ms`);
        setRenderComplete(true);
      })();
    }

    return () => { cancelled = true; clearTimeout(backstop); };
  }, [canRender, dataReady]);

  if (!code) return notice('Missing "code" query parameter.', { color: "#b91c1c", isError: true });
  if (resolved === null) return notice("Resolving report target…");
  if (resolved.error) return notice(`Report render error: ${resolved.error}`, { color: "#b91c1c", isError: true });
  if (tokenState === "invalid") return notice("Invalid or missing render token — report not rendered.", { color: "#b91c1c", isError: true });
  if (tokenState === "checking") return notice("Validating render token…");

  const p = resolved.props;
  const Cmp = COMPONENTS[type];
  if (!Cmp) return notice(`Unsupported report type "${type}".`, { color: "#b91c1c", isError: true });

  return (
    <div className="pdf-pipeline" data-render-complete={renderComplete ? "true" : "false"} style={{ background: "#fff", minHeight: "100vh" }}>
      {/* Pipeline-ONLY box-shadow strip. Chromium page.pdf() rasterizes soft
          box-shadows as hard grey rectangles; the desktop studio avoids this only
          because its html2canvas export drops shadows entirely. Match that: remove
          shadows on everything under .pdf-pipeline — a class mounted ONLY by this
          render route, so the desktop app and live /m keep their on-screen shadows.
          Scoped to the class (not the data-render-complete signal attr); borders/
          outlines are untouched so cards still read as bordered, not floating. */}
      <style dangerouslySetInnerHTML={{ __html: ".pdf-pipeline, .pdf-pipeline * { box-shadow: none !important; }" }} />
      <Cmp
        id="report-render"
        districtName={p.districtName}
        state={p.state}
        stateFips={p.stateFips}
        stateName={p.stateName}
        district={p.district}
        areaCode={p.areaCode}
        areaType={p.areaType}
        clientLabel={clientLabel}
        onReady={handleReady}
      />
    </div>
  );
}
